import { useNavigate } from "react-router-dom";
import {
  Dumbbell, Utensils, Sparkles as Skincare, Wind, Heart, ChevronRight,
  Clock, Sun, Moon, Wand2,
} from "lucide-react";

// Improve hub — the master spec's "✨ Improve" tab.
// Surfaces the 4 self-improvement pillars and links to live screens
// where they exist + clear "coming soon" labels for the rest.
//
// Free users see every category (no blanket locks). Premium gates only
// advanced features INSIDE each pillar.

const PILLARS = [
  {
    key: "fitness",
    title: "Fitness",
    blurb: "Workout plans, muscle gain, weight loss, training tracking.",
    accent: Dumbbell,
    items: [
      { label: "Body Goal Coach",    route: "/improve/body-goal",    live: true },
      { label: "Gym Coach",          route: "/improve/fitness",      live: true },
      { label: "Workout Plans",      route: "/improve/fitness",      live: true },
      { label: "Muscle Gain Plan",   route: "/improve/fitness?goal=gain-muscle", live: true },
      { label: "Weight Loss Plan",   route: "/improve/fitness?goal=lose-weight", live: true },
      { label: "Workout Tracking",   route: "/progress",             live: true },
    ],
    testId: "improve-fitness",
  },
  {
    key: "nutrition",
    title: "Nutrition",
    blurb: "Food scanner, calorie + macro tracking, meal swaps.",
    accent: Utensils,
    items: [
      { label: "Food Scanner",     route: "/nutrition/scan",    live: true },
      { label: "Calorie Targets",  route: "/nutrition/targets", live: true },
      { label: "Macro Tracking",   route: "/nutrition/scan",    live: true },
    ],
    testId: "improve-nutrition",
  },
  {
    key: "skincare",
    title: "Skincare",
    blurb: "Skin analysis, AM/PM routines, products that match your skin.",
    accent: Skincare,
    items: [
      { label: "Skin Analysis",     route: "/improve/skincare", live: true },
      { label: "AM + PM Routine",   route: "/improve/skincare", live: true },
      { label: "Morning Routine",   route: "/improve/skincare", live: true, icon: Sun },
      { label: "Night Routine",     route: "/improve/skincare", live: true, icon: Moon },
      { label: "Skincare Products", route: "/improve/skincare", live: true },
    ],
    testId: "improve-skincare",
  },
  {
    key: "grooming",
    title: "Grooming",
    blurb: "Hair care + beard care. Photo-analysis, real product picks, routines.",
    accent: Wand2,
    items: [
      { label: "Grooming Hub",   route: "/improve/grooming",       live: true },
      { label: "Hair Care",      route: "/improve/grooming/hair",  live: true },
      { label: "Beard Care",     route: "/improve/grooming/beard", live: true },
    ],
    testId: "improve-grooming",
  },
  {
    key: "fragrance",
    title: "Fragrance",
    blurb: "Cologne / perfume recommendations by season and occasion.",
    accent: Wind,
    items: [
      { label: "Men's Cologne",            route: "/improve/fragrance", live: true },
      { label: "Women's Perfume",          route: "/improve/fragrance", live: true },
      { label: "Seasonal Recommendations", route: "/improve/fragrance", live: true },
      { label: "Occasion Recommendations", route: "/improve/fragrance", live: true },
    ],
    testId: "improve-fragrance",
  },
  {
    key: "motivation",
    title: "AI Motivation Coach",
    blurb: "Natural conversations, daily motivation, milestone celebrations.",
    accent: Heart,
    items: [
      { label: "Talk to Coach",     route: "/improve/motivate", live: true },
      { label: "Daily Brief",       route: "/progress",         live: true },
      { label: "Goal Tracking",     route: "/profile-setup",    live: true },
    ],
    testId: "improve-motivation",
  },
];

function PillarCard({ pillar }) {
  const navigate = useNavigate();
  const Accent = pillar.accent;
  return (
    <div className="outfit-section" data-testid={pillar.testId}>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", gap: 14, padding: "18px 20px", alignItems: "center", borderBottom: "1px solid var(--line)" }}>
          <div className="tile-icon" style={{ width: 44, height: 44 }}>
            <Accent size={18} strokeWidth={1.7} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="h3" style={{ color: "var(--text)" }}>{pillar.title}</div>
            <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 3 }}>{pillar.blurb}</div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {pillar.items.map((it, i) => {
            const Icon = it.icon || Wand2;
            const dt = `${pillar.testId}-item-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
            const inner = (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                  <div style={{ width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", color: it.live ? "var(--gold)" : "var(--text-mute)" }}>
                    <Icon size={14} strokeWidth={1.7} />
                  </div>
                  <div className="body" style={{ color: it.live ? "var(--text)" : "var(--text-dim)" }}>{it.label}</div>
                </div>
                {!it.live ? (
                  <span className="tiny" style={{ letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--text-faint)" }}>
                    <Clock size={10} style={{ marginRight: 4 }} /> SOON
                  </span>
                ) : (
                  <ChevronRight size={16} color="var(--text-faint)" />
                )}
              </>
            );
            return it.live && it.route ? (
              <button
                key={i}
                type="button"
                onClick={() => navigate(it.route)}
                data-testid={dt}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                  padding: "14px 20px", background: "transparent", border: "none",
                  borderBottom: i === pillar.items.length - 1 ? "none" : "1px solid var(--line)",
                  cursor: "pointer", textAlign: "left", color: "var(--text)",
                }}
              >
                {inner}
              </button>
            ) : (
              <div
                key={i}
                data-testid={dt}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                  padding: "14px 20px",
                  borderBottom: i === pillar.items.length - 1 ? "none" : "1px solid var(--line)",
                  opacity: 0.72,
                }}
              >
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ImproveScreen() {
  return (
    <div className="screen with-nav fade-in" data-testid="improve-screen">
      <div className="top-bar">
        <div className="logo-mark">IMPROVE</div>
        <span className="premium-pill">FREE</span>
      </div>

      <div className="eyebrow" style={{ marginBottom: 8 }}>YOUR GLOW-UP TOOLKIT</div>
      <h2 className="h1" style={{ fontSize: 30, marginBottom: 14, lineHeight: 1.12 }}>
        Pick your<br />
        <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>next move.</em>
      </h2>
      <p className="body" style={{ marginTop: 0, marginBottom: 18, color: "var(--text-dim)" }}>
        Five pillars. One score. Every feature is accessible — Premium unlocks advanced coaching, unlimited scans, and deeper insights.
      </p>

      {PILLARS.map((p) => <PillarCard key={p.key} pillar={p} />)}
    </div>
  );
}
